'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetUserStreak } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon, ArrowRight01Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { projectThresholdDates, tierBgClass, tierBgSubtleClass } from '@/lib/streaks';
import { cn } from '@/lib/ui/utils';
import { TierEmblem } from './tier-emblem';
interface StreakCalendarSheetProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Combined year*12+month index for a 'YYYY-MM-DD' day — lets month nav clamp with plain math. */
function monthIndexOf(dateStr: string): number {
  const parts = dateStr.split('-');
  return Number(parts[0]) * 12 + (Number(parts[1]) - 1);
}
const zonedDayCache: Map<string, Intl.DateTimeFormat> = new Map();
function getZonedDayFormatter(timezone: string): Intl.DateTimeFormat {
  const cached = zonedDayCache.get(timezone);
  if (cached !== undefined) return cached;
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone
  });
  zonedDayCache.set(timezone, fmt);
  return fmt;
}
const defaultDayFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-CA');
const monthLabelCache: Map<string, Intl.DateTimeFormat> = new Map();
function getMonthLabelFormatter(locale: string): Intl.DateTimeFormat {
  const cached = monthLabelCache.get(locale);
  if (cached !== undefined) return cached;
  const fmt = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric'
  });
  monthLabelCache.set(locale, fmt);
  return fmt;
}
const weekdayNarrowCache: Map<string, Intl.DateTimeFormat> = new Map();
function getWeekdayNarrowFormatter(locale: string): Intl.DateTimeFormat {
  const cached = weekdayNarrowCache.get(locale);
  if (cached !== undefined) return cached;
  const fmt = new Intl.DateTimeFormat(locale, {
    weekday: 'narrow'
  });
  weekdayNarrowCache.set(locale, fmt);
  return fmt;
}

/** 'YYYY-MM-DD' for `now` in the given IANA timezone. */
function todayInZone(timezone: string): string {
  try {
    return getZonedDayFormatter(timezone).format(new Date());
  } catch {
    // SILENT-CATCH: invalid IANA timezone string falls back to the runtime default locale
    return defaultDayFormatter.format(new Date());
  }
}

/** Calendar of qualifying local days; pages across months and projects upcoming tier milestones. */
export function StreakCalendarSheet(t0) {
  const $ = _c(123);
  const {
    userId,
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("streaks");
  const locale = useLocale();
  const [monthOffset, setMonthOffset] = useState(0);
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (open) {
        setMonthOffset(0);
      }
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["currentStreak", "longestStreak", "timezone", {
      days: ["date", "traded"]
    }];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== userId) {
    t4 = {
      identifier: userId,
      fields: t3
    };
    $[4] = userId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== open) {
    t5 = {
      enabled: open
    };
    $[6] = open;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data
  } = useGetUserStreak(t4, t5);
  const timezone = data?.timezone ?? "UTC";
  const currentStreak = data?.currentStreak ?? 0;
  const longestStreak = data?.longestStreak ?? 0;
  let t6;
  if ($[8] !== data?.days) {
    t6 = new Set((data?.days ?? []).flatMap(_temp));
    $[8] = data?.days;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const traded = t6;
  let T0;
  let lastMilestone;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t7;
  let t8;
  let t9;
  if ($[10] !== currentStreak || $[11] !== data?.days || $[12] !== locale || $[13] !== longestStreak || $[14] !== monthOffset || $[15] !== onOpenChange || $[16] !== open || $[17] !== t || $[18] !== timezone || $[19] !== traded) {
    const today = todayInZone(timezone);
    const todayIndex = monthIndexOf(today);
    const milestones = projectThresholdDates(currentStreak, today);
    const milestoneByDate = new Map(milestones.map(_temp2));
    const firstMilestone = milestones[0];
    const historyDates = (data?.days ?? []).map(_temp3);
    const minIndex = historyDates.reduce(_temp4, todayIndex);
    lastMilestone = milestones.at(-1);
    const maxIndex = lastMilestone ? monthIndexOf(lastMilestone.date) : todayIndex;
    const minOffset = minIndex - todayIndex;
    const maxOffset = maxIndex - todayIndex;
    const offset = Math.min(maxOffset, Math.max(minOffset, monthOffset));
    const viewedIndex = todayIndex + offset;
    const year = Math.floor(viewedIndex / 12);
    const month = viewedIndex % 12 + 1;
    let monthLabel;
    let padCells;
    let t20;
    let weekdays;
    if ($[35] !== locale || $[36] !== month || $[37] !== year) {
      const daysInMonth = new Date(year, month, 0).getDate();
      const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
      monthLabel = getMonthLabelFormatter(locale).format(new Date(year, month - 1, 1));
      let t21;
      if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
        $[42] = t21;
      } else {
        t21 = $[42];
      }
      const weekdayKeys = t21 as const;
      let t22;
      if ($[43] !== locale) {
        const weekdayNarrowFormatter = getWeekdayNarrowFormatter(locale);
        t22 = weekdayKeys.map((key, i) => ({
          key,
          label: weekdayNarrowFormatter.format(new Date(2024, 0, 1 + i))
        }));
        $[43] = locale;
        $[44] = t22;
      } else {
        t22 = $[44];
      }
      weekdays = t22;
      padCells = Array.from({
        length: firstWeekday
      }, _temp5);
      t20 = Array.from({
        length: daysInMonth
      }, (__0, i_1) => {
        const d_2 = i_1 + 1;
        return {
          key: `${year}-${pad(month)}-${pad(d_2)}`,
          day: d_2 as number | null
        };
      });
      $[35] = locale;
      $[36] = month;
      $[37] = year;
      $[38] = monthLabel;
      $[39] = padCells;
      $[40] = t20;
      $[41] = weekdays;
    } else {
      monthLabel = $[38];
      padCells = $[39];
      t20 = $[40];
      weekdays = $[41];
    }
    const dayCells = t20;
    const cells = [...padCells, ...dayCells];
    T0 = InfoModal;
    t15 = open;
    t16 = onOpenChange;
    t17 = "default";
    if ($[45] !== currentStreak || $[46] !== t) {
      t18 = t("day_streak", {
        count: currentStreak
      });
      $[45] = currentStreak;
      $[46] = t;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    if ($[48] !== longestStreak || $[49] !== t) {
      t19 = t("longest_label", {
        count: longestStreak
      });
      $[48] = longestStreak;
      $[49] = t;
      $[50] = t19;
    } else {
      t19 = $[50];
    }
    t12 = "flex flex-col gap-3";
    let t21;
    if ($[51] !== t) {
      t21 = t("prev_month");
      $[51] = t;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    const t22 = offset <= minOffset;
    let t23;
    if ($[53] !== offset) {
      t23 = () => setMonthOffset(offset - 1);
      $[53] = offset;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <Icon icon={ArrowLeft01Icon} />;
      $[55] = t24;
    } else {
      t24 = $[55];
    }
    let t25;
    if ($[56] !== t21 || $[57] !== t22 || $[58] !== t23) {
      t25 = <AnimatedButton variant="tertiary" size="sm" aria-label={t21} disabled={t22} onClick={t23}>{t24}</AnimatedButton>;
      $[56] = t21;
      $[57] = t22;
      $[58] = t23;
      $[59] = t25;
    } else {
      t25 = $[59];
    }
    let t26;
    if ($[60] !== monthLabel) {
      t26 = <div className="text-center text-heading-xs">{monthLabel}</div>;
      $[60] = monthLabel;
      $[61] = t26;
    } else {
      t26 = $[61];
    }
    let t27;
    if ($[62] !== t) {
      t27 = t("next_month");
      $[62] = t;
      $[63] = t27;
    } else {
      t27 = $[63];
    }
    const t28 = offset >= maxOffset;
    let t29;
    if ($[64] !== offset) {
      t29 = () => setMonthOffset(offset + 1);
      $[64] = offset;
      $[65] = t29;
    } else {
      t29 = $[65];
    }
    let t30;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <Icon icon={ArrowRight01Icon} />;
      $[66] = t30;
    } else {
      t30 = $[66];
    }
    let t31;
    if ($[67] !== t27 || $[68] !== t28 || $[69] !== t29) {
      t31 = <AnimatedButton variant="tertiary" size="sm" aria-label={t27} disabled={t28} onClick={t29}>{t30}</AnimatedButton>;
      $[67] = t27;
      $[68] = t28;
      $[69] = t29;
      $[70] = t31;
    } else {
      t31 = $[70];
    }
    if ($[71] !== t25 || $[72] !== t26 || $[73] !== t31) {
      t13 = <div className="flex items-center justify-between">{t25}{t26}{t31}</div>;
      $[71] = t25;
      $[72] = t26;
      $[73] = t31;
      $[74] = t13;
    } else {
      t13 = $[74];
    }
    let t32;
    if ($[75] !== weekdays) {
      t32 = weekdays.map(_temp6);
      $[75] = weekdays;
      $[76] = t32;
    } else {
      t32 = $[76];
    }
    t14 = <div className="grid grid-cols-7 gap-1.5">{t32}{cells.map(cell => {
        if (cell.day === null) {
          return <span key={cell.key} />;
        }
        const dateStr = cell.key;
        const milestone = milestoneByDate.get(dateStr);
        if (milestone) {
          return <AppTooltip key={dateStr} content={t("days_until", {
            count: milestone.daysAway,
            tier: milestone.name
          })}><span className={cn("grid aspect-square place-items-center rounded-panel-inset", tierBgSubtleClass(milestone.level))}><TierEmblem level={milestone.level} size={24} alt={t("milestone_aria", {
                tier: milestone.name
              })} /></span></AppTooltip>;
        }
        const isTraded = traded.has(dateStr);
        const isToday = dateStr === today;
        const isFuture = dateStr > today;
        return <span key={dateStr} className={cn("grid aspect-square place-items-center rounded-panel-inset text-caption font-medium", isTraded ? "bg-feedback-success-surface text-feedback-success-content" : isFuture ? "border border-dashed border-border-primary text-foreground-tertiary" : "bg-background-tertiary text-foreground-primary", isToday ? "ring-2 ring-border-brand" : "")}>{cell.day}</span>;
      })}</div>;
    t7 = "flex flex-wrap gap-x-4 gap-y-1 text-micro text-foreground-primary";
    let t33;
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <span className="size-2.5 rounded-item bg-feedback-success-accent" />;
      $[77] = t33;
    } else {
      t33 = $[77];
    }
    let t34;
    if ($[78] !== t) {
      t34 = t("legend_traded");
      $[78] = t;
      $[79] = t34;
    } else {
      t34 = $[79];
    }
    if ($[80] !== t34) {
      t8 = <span className="inline-flex items-center gap-1.5">{t33}{t34}</span>;
      $[80] = t34;
      $[81] = t8;
    } else {
      t8 = $[81];
    }
    let t35;
    if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <span className="size-2.5 rounded-item bg-background-tertiary" />;
      $[82] = t35;
    } else {
      t35 = $[82];
    }
    let t36;
    if ($[83] !== t) {
      t36 = t("legend_missed");
      $[83] = t;
      $[84] = t36;
    } else {
      t36 = $[84];
    }
    if ($[85] !== t36) {
      t9 = <span className="inline-flex items-center gap-1.5">{t35}{t36}</span>;
      $[85] = t36;
      $[86] = t9;
    } else {
      t9 = $[86];
    }
    let t37;
    if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = <span className="size-2.5 rounded-item border border-dashed border-border-primary" />;
      $[87] = t37;
    } else {
      t37 = $[87];
    }
    let t38;
    if ($[88] !== t) {
      t38 = t("legend_upcoming");
      $[88] = t;
      $[89] = t38;
    } else {
      t38 = $[89];
    }
    if ($[90] !== t38) {
      t10 = <span className="inline-flex items-center gap-1.5">{t37}{t38}</span>;
      $[90] = t38;
      $[91] = t10;
    } else {
      t10 = $[91];
    }
    t11 = firstMilestone ? <span className="inline-flex items-center gap-1.5"><span className={cn("size-2.5 rounded-item", tierBgClass(firstMilestone.level))} />{t("legend_milestone")}</span> : null;
    $[10] = currentStreak;
    $[11] = data?.days;
    $[12] = locale;
    $[13] = longestStreak;
    $[14] = monthOffset;
    $[15] = onOpenChange;
    $[16] = open;
    $[17] = t;
    $[18] = timezone;
    $[19] = traded;
    $[20] = T0;
    $[21] = lastMilestone;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    T0 = $[20];
    lastMilestone = $[21];
    t10 = $[22];
    t11 = $[23];
    t12 = $[24];
    t13 = $[25];
    t14 = $[26];
    t15 = $[27];
    t16 = $[28];
    t17 = $[29];
    t18 = $[30];
    t19 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  let t20;
  if ($[92] !== t10 || $[93] !== t11 || $[94] !== t7 || $[95] !== t8 || $[96] !== t9) {
    t20 = <div className={t7}>{t8}{t9}{t10}{t11}</div>;
    $[92] = t10;
    $[93] = t11;
    $[94] = t7;
    $[95] = t8;
    $[96] = t9;
    $[97] = t20;
  } else {
    t20 = $[97];
  }
  let t21;
  if ($[98] !== lastMilestone || $[99] !== t) {
    t21 = lastMilestone ? <span>{t("projection_note")}</span> : null;
    $[98] = lastMilestone;
    $[99] = t;
    $[100] = t21;
  } else {
    t21 = $[100];
  }
  let t22;
  if ($[101] !== t || $[102] !== timezone) {
    t22 = t("day_boundary", {
      zone: timezone
    });
    $[101] = t;
    $[102] = timezone;
    $[103] = t22;
  } else {
    t22 = $[103];
  }
  let t23;
  if ($[104] !== t22) {
    t23 = <span>{t22}</span>;
    $[104] = t22;
    $[105] = t23;
  } else {
    t23 = $[105];
  }
  let t24;
  if ($[106] !== t21 || $[107] !== t23) {
    t24 = <div className="flex flex-col gap-1 border-t border-border-primary pt-3 text-micro text-foreground-tertiary">{t21}{t23}</div>;
    $[106] = t21;
    $[107] = t23;
    $[108] = t24;
  } else {
    t24 = $[108];
  }
  let t25;
  if ($[109] !== t12 || $[110] !== t13 || $[111] !== t14 || $[112] !== t20 || $[113] !== t24) {
    t25 = <div className={t12}>{t13}{t14}{t20}{t24}</div>;
    $[109] = t12;
    $[110] = t13;
    $[111] = t14;
    $[112] = t20;
    $[113] = t24;
    $[114] = t25;
  } else {
    t25 = $[114];
  }
  let t26;
  if ($[115] !== T0 || $[116] !== t15 || $[117] !== t16 || $[118] !== t17 || $[119] !== t18 || $[120] !== t19 || $[121] !== t25) {
    t26 = <T0 open={t15} onOpenChange={t16} size={t17} title={t18} description={t19}>{t25}</T0>;
    $[115] = T0;
    $[116] = t15;
    $[117] = t16;
    $[118] = t17;
    $[119] = t18;
    $[120] = t19;
    $[121] = t25;
    $[122] = t26;
  } else {
    t26 = $[122];
  }
  return t26;
}
function _temp6(wd) {
  return <span key={wd.key} className="text-center text-micro font-medium text-foreground-tertiary">{wd.label}</span>;
}
function _temp5(_, i_0) {
  return {
    key: `pad-${i_0}`,
    day: null as number | null
  };
}
function _temp4(min, d_1) {
  return Math.min(min, monthIndexOf(d_1));
}
function _temp3(d_0) {
  return d_0.date;
}
function _temp2(m) {
  return [m.date, m];
}
function _temp(d) {
  return d.traded ? [d.date] : [];
}